/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AlarmActivation,
  AlarmActivation$inboundSchema,
  AlarmActivation$Outbound,
  AlarmActivation$outboundSchema,
} from "./alarmactivation.js";
import {
  AlarmActivationSummary,
  AlarmActivationSummary$inboundSchema,
  AlarmActivationSummary$Outbound,
  AlarmActivationSummary$outboundSchema,
} from "./alarmactivationsummary.js";

/**
 * Websocket event type
 */
export const AlarmEventType = {
  AlarmActivation: "alarm_activation",
  Error: "error",
  Keepalive: "keepalive",
  Reading: "reading",
  Setting: "setting",
  ShutdownBanner: "shutdown_banner",
  Trends: "trends",
  UserActivity: "user_activity",
} as const;
/**
 * Websocket event type
 */
export type AlarmEventType = OpenEnum<typeof AlarmEventType>;

/**
 * Stream of alarm activations and summaries
 */
export type AlarmEvent = {
  /**
   * The trace metadata, initialized by the trace stream interceptor
   */
  traceMetadata?: { [k: string]: string } | undefined;
  /**
   * Active alarm activation event(s), array for the initial list of active alarms, single for subsequent updates
   */
  activations?: Array<AlarmActivation> | undefined;
  /**
   * Summary of the current alarm activations
   */
  summary?: AlarmActivationSummary | undefined;
  /**
   * Websocket event type
   */
  type?: AlarmEventType | undefined;
};

/** @internal */
export const AlarmEventType$inboundSchema: z.ZodType<
  AlarmEventType,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(AlarmEventType);
/** @internal */
export const AlarmEventType$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  AlarmEventType
> = openEnums.outboundSchema(AlarmEventType);

/** @internal */
export const AlarmEvent$inboundSchema: z.ZodType<
  AlarmEvent,
  z.ZodTypeDef,
  unknown
> = z.object({
  TraceMetadata: types.optional(z.record(types.string())),
  activations: types.optional(z.array(AlarmActivation$inboundSchema)),
  summary: types.optional(AlarmActivationSummary$inboundSchema),
  type: AlarmEventType$inboundSchema.default("alarm_activation"),
}).transform((v) => {
  return remap$(v, {
    "TraceMetadata": "traceMetadata",
  });
});
/** @internal */
export type AlarmEvent$Outbound = {
  TraceMetadata?: { [k: string]: string } | undefined;
  activations?: Array<AlarmActivation$Outbound> | undefined;
  summary?: AlarmActivationSummary$Outbound | undefined;
  type: string;
};

/** @internal */
export const AlarmEvent$outboundSchema: z.ZodType<
  AlarmEvent$Outbound,
  z.ZodTypeDef,
  AlarmEvent
> = z.object({
  traceMetadata: z.record(z.string()).optional(),
  activations: z.array(AlarmActivation$outboundSchema).optional(),
  summary: AlarmActivationSummary$outboundSchema.optional(),
  type: AlarmEventType$outboundSchema.default("alarm_activation"),
}).transform((v) => {
  return remap$(v, {
    traceMetadata: "TraceMetadata",
  });
});

export function alarmEventToJSON(alarmEvent: AlarmEvent): string {
  return JSON.stringify(AlarmEvent$outboundSchema.parse(alarmEvent));
}
export function alarmEventFromJSON(
  jsonString: string,
): SafeParseResult<AlarmEvent, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AlarmEvent$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AlarmEvent' from JSON`,
  );
}
